import {_} from 'lodash';
import {moment} from 'moment';
import {ObjectTypeId} from '../../../constants';
import {IObjectData, IOrganization, IOverride, ISystem, ISystemPermission, ISystemRole} from '../../../index';
import {global} from '../../../appContext';
import {getObjectDisplayName, getSyncFieldValue} from '../../../util/misc';
import {convertFromServerString, convertToServerString} from '../../../util/datetime';
import {IOverrideStatusParams} from '../../override/override-status';
import {nsOverride} from '../../override/nsOverride';
import {getHash, getRouteDefinition, isRoutePermissionOkay} from '../../../constants/RouteDefinitions';
import {nsRoute} from '../../../util/route';
import OverrideValue = nsOverride.OverrideValue;
import getBasicOverride = nsOverride.getBasicOverride;
import getOverrideStatusPillVariant = nsOverride.getOverrideStatusPillVariant;
import getOverrideStatusSuffix = nsOverride.getOverrideStatusSuffix;

export namespace nsUserPermissions {

    /** Altinn (and similar) scopes `managerOf` to a LegalUnit, not a department. */
    export function isLegalUnitScope(systemPermission: ISystemPermission): boolean {
        return systemPermission.managerOfObjectTypeId == ObjectTypeId.LegalUnit;
    }

    /** Grid scope-column header: LegalUnit rows use global `legalunit`, otherwise department. */
    export function scopeColumnHeaderKey(permissions: ISystemPermission[]): string {
        if (permissions.some(isLegalUnitScope)) {
            return 'legalunit';
        }
        return 'user.permissions.column.department';
    }

    /** Trimmed permission-row comment, or empty when unset. */
    export function permissionCommentText(systemPermission: ISystemPermission): string {
        return (systemPermission.comment ?? '').trim();
    }

    /**
     * Combobox-v2 `option.html`: escaped label, plus a second line for a trimmed description when set.
     * Filtering / input value still use `option.label`.
     */
    export function comboboxOptionHtmlWithDescription(
        label: string,
        description: string | null | undefined,
    ): string {
        const safeLabel = _.escape(label ?? '');
        const desc = String(description ?? '').trim();
        if (!desc) {
            return `<span class="permissions-v2-combobox-option">${safeLabel}</span>`;
        }
        return (
            `<span class="permissions-v2-combobox-option">` +
            `<span class="permissions-v2-combobox-option__label">${safeLabel}</span>` +
            `<span class="permissions-v2-combobox-option__description">${_.escape(desc)}</span>` +
            `</span>`
        );
    }

    /** Integration-owned system (`ReadOnly` / `definitionreadonly`) — not editable in the UI. */
    export function isSystemDefinitionReadOnly(system: ISystem | null | undefined): boolean {
        return !!system?.definitionreadonly;
    }

    /** System details href when the actor may open system access; otherwise null. */
    export function systemAccessHref(system: ISystem | null | undefined): string | null {
        if (!system?.id || !isRoutePermissionOkay('systemaccessdetails')) {
            return null;
        }
        return getHash('systemaccessdetails', {id: system.id}, true, system.companyid) ?? null;
    }

    /** LegalUnit details href when licensed and permitted; otherwise null (plain name). */
    export function legalUnitScopeHref(systemPermission: ISystemPermission): string | null {
        if (!isLegalUnitScope(systemPermission) || !systemPermission.managerOf) {
            return null;
        }
        const def = getRouteDefinition('legalunitdetails');
        const org = global.login.selectedOrganization();
        if (!def || !org || !isRoutePermissionOkay(def) || !nsRoute.isRouteLicenseOkay(def, org)) {
            return null;
        }
        return getHash('legalunitdetails', {
            id: systemPermission.managerOf,
            orgnr: systemPermission.companyid,
        }) ?? null;
    }

    /** Department details href when the actor may open departments; otherwise null. */
    export function departmentDetailsHref(departmentId: number, companyid: string): string | null {
        if (!departmentId || !isRoutePermissionOkay('departmentdetails')) {
            return null;
        }
        return getHash('departmentdetails', {id: departmentId}, true, companyid) ?? null;
    }

    /** Server datetime → `<input type="date">` value (`YYYY-MM-DD`). */
    export function convertExpiryDateFromServerString(date: string | null | undefined): string | null {
        if (!date) {
            return null;
        }
        return convertFromServerString(date).format('YYYY-MM-DD');
    }

    /** Date-input value → `updatepermission` expiry (`DD.MM.YYYY HH:mm`). */
    export function convertExpiryDateToServerString(date: string): string {
        if (!date) {
            return '';
        }
        return convertToServerString(moment(date, 'YYYY-MM-DD')) ?? '';
    }

    /**
     * Any getobject / already-normalized expiry → `dd.MM.yyyy HH:mm` for updatepermission.
     * Untouched sibling SPs keep culture ToString() from getobject; BE ParseExact rejects those.
     */
    export function normalizeExpiryDateForUpdate(date: string | null | undefined): string {
        if (!date) {
            return '';
        }
        return convertToServerString(convertFromServerString(date)) ?? '';
    }

    /**
     * Orgs for permissions-v2 panels: accessible getobject orgs, plus any company that only
     * appears via a system override (login org shell), with synthetic AP rows for unpaired overrides.
     */
    export function mergeOrganizationsForPermissionPanels(
        accessibleOrganizations: IOrganization[],
        userOverrides: IOverride[],
    ): IOrganization[] {
        const organizations = _.cloneDeep(accessibleOrganizations);
        const byCompanyId: Record<string, IOrganization> = _.keyBy(
            organizations,
            (o: IOrganization) => o.organizationid,
        );
        const systemOverrides = (userOverrides ?? []).filter(o => o.objecttypeid == ObjectTypeId.System);

        // Companies with override-only grants: add a login org shell if the actor can see it.
        for (const override of systemOverrides) {
            if (!shouldSynthesizePermissionFromOverride(override)) {
                continue;
            }
            if (byCompanyId[override.companyid]) {
                continue;
            }
            const loginOrg = global.login.getOrganizationById(override.companyid);
            if (!loginOrg) {
                continue;
            }
            const org = _.cloneDeep(loginOrg);
            org.systempermission = [syntheticAutomaticPermissionFromOverride(override)];
            organizations.push(org);
            byCompanyId[org.organizationid] = org;
        }

        // Unpaired system overrides → synthetic automatic rows on the matching org.
        for (const override of systemOverrides) {
            if (!shouldSynthesizePermissionFromOverride(override)) {
                continue;
            }
            const org = byCompanyId[override.companyid];
            if (!org) {
                continue;
            }
            // Only an automatic SP represents an override. An MP with the same
            // system+role+dept must not block synthesis — otherwise Modified pairing
            // has no AP to attach the R to after the stored AP is gone from getobject.
            const alreadyRepresented = org.systempermission.some(
                sp => !sp.manual && nsOverride.isSystemPermissionOverridePair(sp, override),
            );
            if (alreadyRepresented) {
                continue;
            }
            org.systempermission.push(syntheticAutomaticPermissionFromOverride(override));
        }

        for (const org of organizations) {
            org.systempermission = _.sortBy(org.systempermission, [
                (sp: ISystemPermission) => sp.system.name.toLowerCase(),
                (sp: ISystemPermission) => sp.role.name.toLowerCase(),
                (sp: ISystemPermission) => sp.managerOf,
            ]);
        }

        return _.sortBy(organizations, (o: IOrganization) => o.organizationame.toLowerCase());
    }

    /** Automatic permission stub used when an override has no matching stored systempermission. */
    function syntheticAutomaticPermissionFromOverride(override: IOverride): ISystemPermission {
        return {
            companyid: override.companyid,
            expirydate: '',
            id: 0,
            managerOf: override.departmentid,
            manual: false,
            recursive: !!override.departmentid,
            role: {id: override.objectid, name: override.sourcename},
            system: {id: override.parentsourceid, name: override.parentsourcename},
        } as ISystemPermission;
    }

    /**
     * Skip terminal synced-deleted overrides — no live SP and nothing left to act on.
     * Still synthesize pending / active remove|add so Pending and Removed can show unpaired rows.
     */
    export function shouldSynthesizePermissionFromOverride(override: IOverride): boolean {
        const suffix = getOverrideStatusSuffix(override);
        return suffix !== 'add.delete.synced' && suffix !== 'remove.delete.synced';
    }

    /** Page status dropdown: Current / Pending / Added / Removed. */
    export type TPermissionStatusFilter = 'current' | 'pending' | 'removed';

    /** Minimal row shape for status-filter matching (PermissionRow fields). */
    export interface IPermissionStatusFilterRow {
        isManual: boolean;
        /** Stored SP id; synthetics from unpaired overrides use `0`. */
        systemPermissionId: number;
        ownOverride: IOverride | null;
        pairedRemoveOverride: IOverride | null;
    }

    /** Whether a permission row belongs on the selected status filter. */
    export function permissionMatchesStatusFilter(
        row: IPermissionStatusFilterRow,
        filter: TPermissionStatusFilter,
    ): boolean {
        switch (filter) {
            case 'current':
                // Real systempermission[] rows only — not override-only synthetic stubs (id 0).
                return !!row.systemPermissionId;
            case 'removed':
                return !row.isManual
                    && !!row.ownOverride
                    && getOverrideStatusSuffix(row.ownOverride) === 'remove.synced';
            case 'pending': {
                const overrides = [row.ownOverride, row.pairedRemoveOverride].filter(
                    (o): o is IOverride => !!o,
                );
                return overrides.some(o => !o.syncronizedimportfileid);
            }
        }
    }

    /**
     * Count collapsed-visible SPs in one org that match a status filter
     * (same rules as the page badges / accordion caret).
     */
    export function countPermissionsMatchingStatusFilter(
        systemPermissions: ISystemPermission[],
        overrides: IOverride[],
        filter: TPermissionStatusFilter,
    ): number {
        const allPermissions = systemPermissions ?? [];
        const overrideList = overrides ?? [];
        return filterVisibleSystemPermissions(allPermissions, overrideList).filter(sp => {
            const ownOverride = sp.manual
                ? null
                : (overrideList.find(o => nsOverride.isSystemPermissionOverridePair(sp, o)) ?? null);
            const pairedRemoveOverride = sp.manual
                ? findPairedAutomaticRemoveOverride(sp, allPermissions, overrideList)
                : null;
            return permissionMatchesStatusFilter({
                isManual: !!sp.manual,
                systemPermissionId: sp.id ?? 0,
                ownOverride,
                pairedRemoveOverride,
            }, filter);
        }).length;
    }

    /**
     * Permission override status for the shared pill: same purpose/state suffixes as
     * membership (`user.permissions.override.*`) plus shared A/R pill chrome from nsOverride.
     */
    export function getPermissionOverrideDisplay(override: IOverride): IOverrideStatusParams {
        return {
            translationKey: `user.permissions.override.${getOverrideStatusSuffix(override)}`,
            pillVariant: getOverrideStatusPillVariant(override),
            hasComment: !!override.comment?.trim(),
        };
    }

    /** Pill for collapsed AP+active-R + MP pair — Figma “Modified” (not remove.*). */
    export function getModifiedPermissionOverrideDisplay(override: IOverride): IOverrideStatusParams {
        return {
            translationKey: 'user.permissions.override.modified',
            pillVariant: 'info',
            hasComment: !!override.comment?.trim(),
        };
    }

    /** Active remove override (`R` / `R`+dept) — not soft-deleted. */
    export function hasActiveRemoveOverride(override: IOverride | null | undefined): boolean {
        return !!override?.value?.startsWith(OverrideValue.Remove) && !override.deletedbyid;
    }

    /** Same grant identity as legacy `isMultipleEqual` / override pairing (dept 0/null aligned). */
    export function sameSystemRoleDepartment(a: ISystemPermission, b: ISystemPermission): boolean {
        return a.system?.id == b.system?.id
            && a.role?.id == b.role?.id
            && (a.managerOf ?? 0) == (b.managerOf ?? 0);
    }

    /**
     * v2 UI: hide an automatic permission that has an active `R` when a manual permission
     * exists for the same system + role + department. Keeps the MP row (“By user”).
     */
    export function filterVisibleSystemPermissions(
        systemPermissions: ISystemPermission[],
        overrides: IOverride[],
    ): ISystemPermission[] {
        const manuals = systemPermissions.filter(sp => sp.manual);
        return systemPermissions.filter(sp => {
            if (sp.manual) {
                return true;
            }
            const override = overrides.find(o => nsOverride.isSystemPermissionOverridePair(sp, o));
            if (!hasActiveRemoveOverride(override)) {
                return true;
            }
            return !manuals.some(mp => sameSystemRoleDepartment(sp, mp));
        });
    }

    /** Active `R` on an AP that matches this MP’s system + role + department (collapse pair). */
    export function findPairedAutomaticRemoveOverride(
        manual: ISystemPermission,
        allPermissions: ISystemPermission[],
        overrides: IOverride[],
    ): IOverride | null {
        const pairedAp = allPermissions.find(sp => !sp.manual && sameSystemRoleDepartment(sp, manual));
        if (!pairedAp) {
            return null;
        }
        const override = overrides.find(o => nsOverride.isSystemPermissionOverridePair(pairedAp, o));
        return hasActiveRemoveOverride(override) ? (override ?? null) : null;
    }

    /**
     * MP draft that matches a soft-removed AP (system+role+dept) with no expiry → auto-restore candidate.
     * Any expiry excludes equality; comment and other fields are ignored.
     */
    export function findAutoRestoreOverrideForManualDraft(
        manualDraft: ISystemPermission,
        allPermissions: ISystemPermission[],
        overrides: IOverride[],
    ): IOverride | null {
        if (!manualDraft.manual) {
            return null;
        }
        if ((manualDraft.expirydate ?? '').trim()) {
            return null;
        }
        return findPairedAutomaticRemoveOverride(manualDraft, allPermissions, overrides);
    }

    /**
     * DisplayName of who added this role via AddPermission history.
     * REFERENCE is scoped to one object type — try User, then ApiUser for integration assigners.
     */
    export function buildManualPermissionAssigneeExpression(
        roleId: number,
        objectTypeId: ObjectTypeId = ObjectTypeId.User,
    ): string {
        return `[REFERENCE;DisplayName;Id;${objectTypeId};[LTRIM;[SPLIT;[SPLIT;[OBJECTHISTORIES;Comment;;EntryType=AddPermission&NewValue=${roleId};true];|;0];:;last]; ]]`;
    }

    /**
     * Prefer evaluateexpression result; if empty, paired AP remove-override `overriddenby`.
     * Returns a display name, or null when the UI should show the translated `By user` key.
     */
    export function resolveManualAssignedByDisplayName(params: {
        evaluatedName: string | null | undefined;
        manual: ISystemPermission;
        allPermissions: ISystemPermission[];
        overrides: IOverride[];
    }): string | null {
        const evaluated = params.evaluatedName?.trim();
        if (evaluated) {
            return evaluated;
        }
        const pairedOverride = findPairedAutomaticRemoveOverride(
            params.manual,
            params.allPermissions,
            params.overrides,
        );
        const fromOverride = pairedOverride?.overriddenby?.trim();
        if (fromOverride) {
            return fromOverride;
        }
        return null;
    }

    /** `createoverride` payload that strips an automatic system permission. */
    export function buildRemoveOverrideForPermission(
        systemPermission: ISystemPermission,
        user: IObjectData,
    ): IOverride {
        const role = systemPermission.role;
        const system = systemPermission.system;
        return _.defaultsDeep({
            companyid: system.companyid ?? systemPermission.companyid,
            objectid: role.id,
            objecttypeid: ObjectTypeId.System,
            parentsourceid: system.id,
            parentsourcename: system.name,
            sourcename: role.name,
            targetname: getObjectDisplayName(user) ?? '',
            targetobjectid: user.id,
            targetobjectsrc: getSyncFieldValue('SourceId', user.syncfields) ?? '',
            targetobjecttypeid: user.type.id,
            value: OverrideValue.Remove + (systemPermission.managerOf || ''),
        } as IOverride, getBasicOverride(system.companyid ?? systemPermission.companyid));
    }

    /** Manual permission row for updatepermission (roles array stripped like legacy). */
    export function buildManualSystemPermissionPayload(params: {
        companyid: string;
        role: ISystemRole;
        system: ISystem;
        departmentId: number;
        departmentName: string;
        expirydateInput: string;
        comment?: string;
        id?: number;
        managerOfObjectTypeId?: ObjectTypeId;
    }): ISystemPermission {
        const systemWithoutRoles = _.clone(params.system);
        delete systemWithoutRoles.roles;
        return {
            comment: (params.comment ?? '').trim(),
            companyid: params.companyid,
            expirydate: convertExpiryDateToServerString(params.expirydateInput),
            id: params.id ?? 0,
            managerOf: params.departmentId,
            managerOfName: params.departmentName,
            managerOfObjectTypeId: params.managerOfObjectTypeId ?? ObjectTypeId.Department,
            manual: true,
            recursive: !!params.role.recursive,
            role: params.role,
            system: systemWithoutRoles as ISystem,
        };
    }

    /**
     * updatepermission jobject: user id + full org permission arrays.
     * Normalizes every SP: expiry → dd.MM.yyyy HH:mm, strip system.roles (payload size).
     */
    export function buildUpdatePermissionJobject(
        user: IObjectData,
        organizations: IOrganization[],
    ): Partial<IObjectData> {
        return {
            id: user.id,
            permission: organizations.map(o => _.defaults({
                systempermission: (o.systempermission ?? []).map(sp => {
                    const systemWithoutRoles = sp.system ? _.clone(sp.system) : sp.system;
                    if (systemWithoutRoles) {
                        delete systemWithoutRoles.roles;
                    }
                    return _.defaults({
                        expirydate: normalizeExpiryDateForUpdate(sp.expirydate),
                        system: systemWithoutRoles,
                    }, sp);
                }),
            }, o)),
        };
    }

    export function invalidatePermissionCaches(): void {
        global.cacheManager.removeCache('getruleset');
        global.cacheManager.removeCache('searchruleset');
        global.cacheManager.removeCache('searchobject');
        global.cacheManager.removeCache('getobject');
        global.cacheManager.removeCache('searchsystem');
    }
}
